// Record a voice note in the browser (owner, 2026-09-25, A2: « SEND: … voice
// note »). The browser records what it can — Opus in WebM (Chrome), Ogg
// (Firefox), MP4 (Safari); the relay converts it to WhatsApp's Ogg/Opus voice
// bubble before sending, so the format here only has to be something ffmpeg
// reads.
import { useCallback, useEffect, useRef, useState } from 'react';

const PREFERRED = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export type Recording = { blob: Blob; mime: string; seconds: number; url: string };

export function useVoiceRecorder() {
  const [state, setState] = useState<'idle' | 'recording' | 'recorded'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState<Recording | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedRef = useRef(0);
  const cancelledRef = useRef(false);

  const stopTracks = () => recRef.current?.stream.getTracks().forEach((t) => t.stop());
  const clearTimer = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };

  const start = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Ce navigateur ne peut pas enregistrer de message vocal.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = PREFERRED.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stopTracks();
        clearTimer();
        if (cancelledRef.current) { setState('idle'); return; }
        const type = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        setRecording({ blob, mime: type, seconds: Math.round((Date.now() - startedRef.current) / 1000), url: URL.createObjectURL(blob) });
        setState('recorded');
      };
      recRef.current = rec;
      startedRef.current = Date.now();
      setSeconds(0);
      rec.start();
      setState('recording');
      timerRef.current = setInterval(() => setSeconds(Math.round((Date.now() - startedRef.current) / 1000)), 250);
    } catch (e) {
      setError((e as Error).name === 'NotAllowedError'
        ? 'Le micro est bloqué — autorise-le dans le navigateur.'
        : `Micro indisponible : ${(e as Error).message}`);
      setState('idle');
    }
  }, []);

  const stop = useCallback(() => { if (recRef.current?.state === 'recording') recRef.current.stop(); }, []);

  const discard = useCallback(() => {
    if (recRef.current?.state === 'recording') { cancelledRef.current = true; recRef.current.stop(); }
    setRecording((r) => { if (r) URL.revokeObjectURL(r.url); return null; });
    setState('idle');
    setSeconds(0);
  }, []);

  useEffect(() => () => { clearTimer(); stopTracks(); }, []);

  return { state, seconds, error, recording, start, stop, discard };
}
